import { breakpointsTailwind } from '@vueuse/core'
import { defaultExportedChart, defineChart } from '@/charts'
import { tooltip } from '@/composables/useChart/theme'

type Params = [imports: Import[], country?: string]

/**
 * Create the world bar chart options.
 */
export default defineChart<Params>((imports, selectedCountry) => {
    const { meta } = useMeta()
    const { getName } = useCountries()
    const format = importsValueFomatter()
    const labelSize = 150

    const sortedCountries = imports
        .map(a => {
            const total = a.products.reduce(
                (total, { import_value }) => total + import_value,
                0
            )

            return {
                country: a.country,
                products: a.products,
                total
            }
        })
        .sort((a, b) => a.total - b.total)

    return {
        grid: {
            top: '2%',
            bottom: '2%'
        },
        xAxis: {
            type: 'value',
            show: false
        },
        export: defaultExportedChart(
            {
                grid: {
                    top: '10%',
                    bottom: '15%',
                    left: labelSize + 20,
                    right: labelSize + 20,
                    containLabel: false
                }
            },
            meta.products
                .map(({ name, color }) => ({
                    label: name,
                    value: name,
                    color
                }))
                .reverse()
        ),
        yAxis: [
            {
                type: 'category',
                data: sortedCountries.map(({ country }) => country),
                triggerEvent: true,
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                },
                axisLabel: {
                    show: true,
                    align: 'left',
                    fontSize: 14,
                    margin: labelSize,
                    width: labelSize,
                    rich: {
                        a: {
                            fontSize: 15,
                            fontWeight: 'bold'
                        }
                    },
                    formatter(value: string) {
                        const name = getName(String(value)) || ''

                        if (selectedCountry === value) {
                            return `{a|${name}}`
                        }

                        return name
                    }
                }
            },
            {
                silent: true,
                type: 'category',
                data: sortedCountries.map(({ total }) => total),
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                },
                position: 'right',
                axisLabel: {
                    show: true,
                    align: 'left',
                    fontSize: 14,
                    rich: {
                        a: {
                            fontSize: 15,
                            fontWeight: 'bold'
                        }
                    },
                    formatter(value: number, index: number) {
                        const country = sortedCountries[index]?.country
                        const text = format(value)

                        if (selectedCountry === country) {
                            return `{a|${text}}`
                        }

                        return text
                    }
                }
            }
        ],
        tooltip: {},
        series: meta.products.flatMap(({ name, color, id }) => {
            return {
                type: 'bar',
                barCategoryGap: '15%',
                tooltip: {
                    formatter(params) {
                        return tooltip(
                            '',
                            String(params.seriesName),
                            format(Number(params.value))
                        )
                    }
                },
                name,
                stack: 'all',
                itemStyle: {
                    color
                },
                data: sortedCountries.map(({ country, products }) => {
                    const value = products
                        .filter(p => p.product_id === id)
                        .reduce((a, b) => a + (b.import_value || 0), 0)

                    return {
                        name: country,
                        value: value
                    }
                })
            }
        }),
        media: [
            {
                query: {
                    maxWidth: breakpointsTailwind.md
                },
                option: {
                    grid: {
                        left: labelSize / 1.5,
                        right: 0,
                        containLabel: false
                    },
                    yAxis: [
                        {
                            axisLabel: {
                                fontSize: 11,
                                margin: labelSize / 1.5,
                                width: labelSize / 1.5
                            }
                        },
                        { show: false }
                    ]
                }
            },
            {
                query: {
                    minWidth: breakpointsTailwind.md
                },
                option: {
                    grid: {
                        left: labelSize,
                        right: labelSize,
                        containLabel: false
                    },
                    yAxis: [
                        {
                            axisLabel: {
                                fontSize: 14,
                                margin: labelSize,
                                width: labelSize
                            }
                        },
                        { show: true }
                    ]
                }
            }
        ]
    }
})
